import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Box, Button, PasswordInput, Stack, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { z } from 'zod';
import { zodResolver } from '../lib/zod-resolver';
import { signIn, verifyTotp } from '../lib/auth';
import { API_BASE } from '../lib/trpc';
import classes from './LoginPage.module.css';

const credentialsSchema = z.object({
  email: z.email('Введіть коректний email'),
  password: z.string().min(1, 'Введіть пароль'),
});

const totpSchema = z.object({
  code: z.string().regex(/^\d{6}$/u, 'Код складається з 6 цифр'),
});

const SSO_ERRORS: Record<string, string> = {
  unknown_user:
    'Ця пошта пройшла SSO, але користувача з нею в CRM немає. Попросіть адміністратора завести доступ.',
  inactive: 'Обліковий запис деактивовано.',
  invalid_token: 'SSO не підтвердив вхід. Спробуйте ще раз.',
  no_token: 'SSO не повернув токен.',
  session_failed: 'Не вдалося створити сесію після SSO.',
};

interface SsoArrival {
  stage: 'credentials' | 'totp';
  error: string | null;
  /** Shown above the code field when the SSO callback left a pending TOTP challenge. */
  hint: string | null;
}

/**
 * Читає `?sso=…`, який лишає редірект брокера.
 *
 * `totp` — не помилка: SSO довів перший фактор, API видав челендж і чекає на код, як після
 * пароля (`sso-challenge.ts`). Сторінка відкривається одразу на кроці коду — форми з поштою
 * й паролем тут немає, бо повертатись до неї нема куди. Решта кодів — відмови брокера, які
 * перекладаються у зрозумілий текст над формою входу.
 */
function ssoArrivalFromLocation(): SsoArrival {
  const none: SsoArrival = { stage: 'credentials', error: null, hint: null };
  if (typeof window === 'undefined') return none;
  const code = new URLSearchParams(window.location.search).get('sso');
  if (code === null) return none;
  if (code === 'totp') {
    return {
      stage: 'totp',
      error: null,
      hint: 'Вхід через корпоративний SSO підтверджено. Введіть код із застосунку автентифікації.',
    };
  }
  return {
    stage: 'credentials',
    error: SSO_ERRORS[code] ?? 'Вхід через SSO не вдався.',
    hint: null,
  };
}

/**
 * Invite-only sign-in: there is no registration link because the API has no sign-up route.
 * TOTP is a second step rather than a second page so the browser back button cannot land
 * the user in a half-authenticated state.
 */
export function LoginPage(): React.JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  /**
   * Брокер повертає результат як `?sso=…`, а не як тіло відповіді: редірект не має де нести
   * JSON. Мовчазне повернення на форму виглядало б як «нічого не сталося».
   */
  const [arrival] = useState<SsoArrival>(ssoArrivalFromLocation);
  const [stage, setStage] = useState<'credentials' | 'totp'>(arrival.stage);
  const [error, setError] = useState<string | null>(arrival.error);
  const [busy, setBusy] = useState(false);

  const credentials = useForm({
    initialValues: { email: '', password: '' },
    validate: zodResolver(credentialsSchema),
  });

  const totp = useForm({
    initialValues: { code: '' },
    validate: zodResolver(totpSchema),
  });

  /**
   * Drop everything cached for the anonymous state, then go to the dashboard.
   *
   * `refetchType: 'none'` matters: the default awaits a refetch of every active query before
   * resolving, so one slow request leaves the submit button spinning with no error and no
   * navigation — a login that appears to hang for a reason the user cannot see. Marking the
   * entries stale is enough; the destination screen refetches what it needs.
   */
  const finish = async (): Promise<void> => {
    await queryClient.invalidateQueries({ refetchType: 'none' });
    await navigate({ to: '/' });
  };

  const submitCredentials = credentials.onSubmit(async (values) => {
    setBusy(true);
    setError(null);
    try {
      const result = await signIn(values.email, values.password);
      if (!result.ok) {
        setError(result.error ?? 'Не вдалося увійти.');
        return;
      }
      if (result.twoFactorRequired) {
        setStage('totp');
        return;
      }
      await finish();
    } finally {
      setBusy(false);
    }
  });

  const submitTotp = totp.onSubmit(async (values) => {
    setBusy(true);
    setError(null);
    try {
      const result = await verifyTotp(values.code);
      if (!result.ok) {
        setError(result.error ?? 'Невірний код.');
        return;
      }
      await finish();
    } finally {
      setBusy(false);
    }
  });

  return (
    <Box className={classes.root}>
      <Box className={classes.card} data-testid="login-card">
        <Stack gap={4} mb="lg">
          <Box className={classes.logo}>S</Box>
          <Text className={classes.title}>SEO Ops Core</Text>
          <Text className={classes.subtitle}>
            {stage === 'credentials' ? 'вхід за запрошенням' : 'двофакторна автентифікація'}
          </Text>
        </Stack>

        {stage === 'credentials' ? (
          <form onSubmit={submitCredentials} noValidate>
            <Stack gap="sm">
              <TextInput
                label="Email"
                type="email"
                autoComplete="username"
                data-testid="login-email"
                {...credentials.getInputProps('email')}
              />
              <PasswordInput
                label="Пароль"
                autoComplete="current-password"
                data-testid="login-password"
                {...credentials.getInputProps('password')}
              />
              {error ? (
                <Text className={classes.error} data-testid="login-error" role="alert">
                  {error}
                </Text>
              ) : null}
              <Button type="submit" loading={busy} fullWidth data-testid="login-submit">
                Увійти
              </Button>
              <Button
                component="a"
                href={`${API_BASE}/api/auth/sso/start`}
                variant="default"
                fullWidth
                data-testid="login-sso"
              >
                Увійти через корпоративний SSO
              </Button>
            </Stack>
          </form>
        ) : (
          <form onSubmit={submitTotp} noValidate>
            <Stack gap="sm">
              {arrival.hint ? (
                <Text size="sm" data-testid="login-sso-hint">
                  {arrival.hint}
                </Text>
              ) : null}
              <TextInput
                label="Код з застосунку"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                data-testid="login-totp"
                {...totp.getInputProps('code')}
              />
              {error ? (
                <Text className={classes.error} data-testid="login-error" role="alert">
                  {error}
                </Text>
              ) : null}
              <Button type="submit" loading={busy} fullWidth data-testid="totp-submit">
                Підтвердити
              </Button>
            </Stack>
          </form>
        )}
      </Box>
    </Box>
  );
}
